import React from "react";
//#region redux
import { useParams } from "react-router-dom";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  PageLayouts,
  EntityTitle,
  useMountedState,
  useNavigationHistory,
  RouteBreadCrumb,
  Skeletons,
  LoadingWithLogo,
  ViewPanel,
  GenericCommentsView,
} from "components/common";
import { ErrorView } from "components/error";
import {
  CashReconciliationStateFlow,
  CashReconciliationDetailsFormContainer,
  useCashReconciliationPermissions,
} from "components/cashReconciliation";
import { Vouchers } from "components/voucher";
import { useCashReconciliationService } from "serviceHooks";
import useCashReconciliationFileViewer from "./useFileViewer";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CashReconciliationView: React.FC = () => {
  const cashReconciliationService = useCashReconciliationService();
  const { canViewCashReconciliations } = useCashReconciliationPermissions();

  const { getMounted } = useMountedState();
  const urlParams = useParams<EntityIdParams>();
  const formRef = React.useRef<FormHandles>(null);

  const { goBack } = useNavigationHistory();
  const [loading, setLoading] = React.useState(true);
  const [cashRegister, setCashRegister] = React.useState<WintSuperkollAPIModel.CashReconciliationInstance>();
  const [key, setKey] = React.useState(0);
  const displayType = StyleEnums.DisplayType.Info;

  const loadCashRegister = (cashRegisterId: number) => {
    cashReconciliationService
      .get(cashRegisterId)
      .then((result) => {
        if (getMounted()) {
          setCashRegister(result);
          setKey(key + 1);
        }
      })
      .catch(() => {
        if (cashRegister)
          setTimeout(() => {
            goBack();
          }, 1000);
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    const cashRegisterId = urlParams.id;
    if (cashRegisterId) {
      if (cashRegister?.id !== Number.parseInt(cashRegisterId)) {
        setLoading(true);
        loadCashRegister(Number.parseInt(cashRegisterId));
      }
    } else {
      setCashRegister(undefined);
      setLoading(false);
    }
  }, [urlParams]);

  const upperLeftComponent = React.useMemo(
    () =>
      cashRegister ? (
        <EntityTitle
          status={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CashReconciliation.StateFlowState_1)}
          subTitle={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.CashReconciliation.CashReconciliation
          )}
          title={cashRegister.date ? LocalizationHelperInstance.getFormattedDate(cashRegister.date) : ""}
        />
      ) : (
        <></>
      ),
    [cashRegister]
  );

  const upperRightComponent = React.useMemo(
    () =>
      cashRegister ? (
        <CashReconciliationStateFlow key={`stateFlow-cashRegisterState`} id={cashRegister.id} />
      ) : (
        <Skeletons.StateFlowSkeleton key="steps-skeleton" stepCount={3} />
      ),
    [cashRegister]
  );

  const fileViewerComponent = useCashReconciliationFileViewer(key, cashRegister?.id, cashRegister?.attachments);

  return (
    <>
      <PageLayouts.EntityFullScreen
        entityKey={`cash-reconciliation`}
        id={cashRegister?.id ?? 0}
        navigation={
          <RouteBreadCrumb
            current={
              loading ? "" : cashRegister?.date ? LocalizationHelperInstance.getFormattedDate(cashRegister.date) : ""
            }
            showHome
          />
        }
        hasPermissions={canViewCashReconciliations()}
      >
        {loading ? (
          <LoadingWithLogo />
        ) : cashRegister ? (
          <PageLayouts.EntityViewLayout
            displayType={displayType}
            upperLeftComponent={upperLeftComponent}
            upperRightComponent={upperRightComponent}
            lowerLeftComponent={fileViewerComponent}
          >
            <>
              <ViewPanel
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
                icon={IconDefinitionConstants.WEdit}
                classes={{ content: cssExports.detailsFormContainer }}
                children={
                  key > 0 ? (
                    <CashReconciliationDetailsFormContainer key={key} ref={formRef} cashRegister={cashRegister} />
                  ) : (
                    <></>
                  )
                }
              />
              <Vouchers parentId={cashRegister.id} getVouchersHandler={cashReconciliationService.getTransactions} />
              <GenericCommentsView
                key={`comments`}
                parentId={cashRegister.id}
                type={WintSuperkollAPIModel.GenericCommentType.CashReconciliation}
              />
            </>
          </PageLayouts.EntityViewLayout>
        ) : (
          <ErrorView />
        )}
      </PageLayouts.EntityFullScreen>
    </>
  );
};

export default CashReconciliationView;
